import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  Jaw,
  Perio,
  PerioMeasurement,
  PerioMeasurementsBySide,
  PerioMeasurementsBySideDraft,
  Tooth,
  ToothPerioDraft,
  ToothSide,
} from '@libs/ui-lib';
import { deepMergeObjectVersions } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { animate, style, transition, trigger } from '@angular/animations';

import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import {
  LOWER_LEFT_START,
  LOWER_RIGHT_START,
  UPPER_RIGHT_END,
} from '../perio-add-treatment-screen/perio-position-indicator/perio-position-indicator-constants';
import { PerioConfigComponent } from './components';
import {
  PerioJawMeasurements,
  PerioJawRelatedMeasurementsComponent,
} from './components/perio-jaw-related-measurements/perio-jaw-related-measurements.component';
import { PerioFormToothNavigatorComponent } from './components/perio-form-tooth-navigator/perio-form-tooth-navigator.component';
import { PerioExamToothComponent } from './components/perio-exam-tooth';
import { TargetIndexMovement } from './components/perio-config/perio-config.vm';
import { PerioCheatSheetComponent } from './components/perio-cheat-sheet/perio-cheat-sheet.component';
import { PerioNextPocketDepthComponent } from './components/perio-next-pocket-depth/perio-next-pocket-depth.component';
import { HasFgPipe } from './pipes/has-fg.pipe';
import { PerioFormControlService, PerioInputMode } from './services/perio-form-control/perio-form-control.service';

const DEFAULT_PERIO_MEASUREMENT_NUMBER: PerioMeasurement<number> = {
  central: 0,
  distal: 0,
  mesial: 0,
};

const DEFAULT_PERIO_MEASUREMENT_BOOLEAN: PerioMeasurement<boolean> = {
  central: false,
  distal: false,
  mesial: false,
};

const DEFAULT_PERIO_TOOTH_SIDE: ToothSide = 'buccal';

export const DEFAULT_PERIO_MEASUREMENT_BY_SIDE: PerioMeasurementsBySide = {
  bleedingOnProbing: DEFAULT_PERIO_MEASUREMENT_BOOLEAN,
  clinicalAttachmentLoss: DEFAULT_PERIO_MEASUREMENT_NUMBER,
  furcation: DEFAULT_PERIO_MEASUREMENT_NUMBER,
  gingivalMargin: DEFAULT_PERIO_MEASUREMENT_NUMBER,
  pocketDepth: DEFAULT_PERIO_MEASUREMENT_NUMBER,
  suppuration: DEFAULT_PERIO_MEASUREMENT_BOOLEAN,
};

@Component({
  selector: 'ffd-perio-active-tooth-form',
  templateUrl: './perio-active-tooth-form.component.html',
  styleUrls: ['./perio-active-tooth-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PerioConfigComponent,
    PerioJawRelatedMeasurementsComponent,
    PerioFormToothNavigatorComponent,
    PerioExamToothComponent,
    PerioCheatSheetComponent,
    PerioNextPocketDepthComponent,
    SvgIconComponent,
    HasFgPipe,
    LottieComponent,
  ],
  standalone: true,
  animations: [
    trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('300ms 150ms ease-in-out', style({ opacity: 1 }))])]),
  ],
})
export class PerioActiveToothFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly perioFormControlService = inject(PerioFormControlService);

  public tooth = input<ToothPerioDraft>();
  public activeJaw = input<Jaw>();
  public teeth = input.required<Tooth[]>();
  public currentSide = input<ToothSide>(DEFAULT_PERIO_TOOTH_SIDE);
  public isFirstTooth = input<boolean>(false);
  public isLastTooth = input<boolean>(false);
  public isVoiceActive = input.required<boolean>();
  public isCentralRecession = input.required<boolean>();
  public cheatSheetOpened = input.required<boolean>();
  public visualDirection = input.required<'left' | 'right'>();
  public nextTooth = input.required<Tooth | null>();
  public previousTooth = input.required<Tooth | null>();
  public isAiThinking = input<boolean>(false);
  public shouldSlowDown = input<boolean>(false);
  public toothSidePerioDone = output<TargetIndexMovement>();
  public perioChange = output<PerioMeasurementsBySideDraft>();
  public jawMeasurementsChange = output<PerioJawMeasurements>();
  public moveToNextTooth = output<void>();
  public moveToPreviousTooth = output<void>();
  public switchToLingual = output<void>();
  public switchToFacial = output<void>();
  public switchToOppositeJawLingual = output<number>();
  public toggleCheatSheet = output<void>();

  public readonly lottieThinkingOptions: AnimationOptions = {
    path: '/assets/animations/perio-ai-thinking.json',
  };

  // eslint-disable-next-line unicorn/no-useless-undefined
  protected modifiedTooth = signal<ToothPerioDraft | undefined>(undefined);
  protected isTopConfigDisabled = computed(
    () => (!this.reveresedJawOrder() && this.currentSide() === 'lingual') || (this.reveresedJawOrder() && this.currentSide() === 'buccal'),
  );
  protected isBottomConfigDisabled = computed(() => !this.isTopConfigDisabled());

  /**
   * I have to use deepmerge within this signal to replace undefined values by defaults
   * and therefore to remain compatible with the perio teeth library
   *  */
  protected typeSafeModifedTooth = computed(() => {
    const modifiedTooth = this.modifiedTooth() as Tooth;
    const perio: Perio = {
      buccal: deepMergeObjectVersions<PerioMeasurementsBySide>(DEFAULT_PERIO_MEASUREMENT_BY_SIDE, modifiedTooth.perio.buccal),
      lingual: deepMergeObjectVersions<PerioMeasurementsBySide>(DEFAULT_PERIO_MEASUREMENT_BY_SIDE, modifiedTooth.perio.lingual),
    };

    return {
      ...modifiedTooth,
      perio,
    };
  });
  protected buccal = computed(() => this.modifiedTooth()?.perio?.buccal);
  protected lingual = computed(() => this.modifiedTooth()?.perio?.lingual);
  protected reveresedJawOrder = computed(() => {
    const position = this.modifiedTooth()!.position;

    return position >= LOWER_LEFT_START;
  });
  protected reversedMeasurement = computed(() => {
    const position = this.modifiedTooth()!.position;

    return position > UPPER_RIGHT_END && position < LOWER_RIGHT_START ? true : false;
  });

  /**
   * Calculate which side of the next tooth will be measured.
   * In the perio exam traversal, you stay on the same side while moving through different teeth,
   * but switch sides when:
   * 1. Returning to the same tooth position
   * 2. Transitioning between jaws (upper ↔ lower)
   */
  protected nextToothSide = computed(() => {
    const next = this.nextTooth();
    const current = this.modifiedTooth();
    if (!next || !current) return this.currentSide();

    // If same tooth position, next side is opposite of current
    if (next.position === current.position) {
      return this.currentSide() === 'buccal' ? 'lingual' : 'buccal';
    }

    // Check if we're transitioning between jaws (upper: 1-16, lower: 17-32)
    const currentIsUpperJaw = current.position <= UPPER_RIGHT_END;
    const nextIsUpperJaw = next.position <= UPPER_RIGHT_END;
    const isJawTransition = currentIsUpperJaw !== nextIsUpperJaw;

    // If transitioning between jaws, switch sides
    if (isJawTransition) {
      return this.currentSide() === 'buccal' ? 'lingual' : 'buccal';
    }

    // If different tooth position within same jaw, stay on the same side
    return this.currentSide();
  });

  /**
   * Calculate which side of the previous tooth was measured.
   * In the perio exam traversal, you stay on the same side while moving through different teeth,
   * but switch sides when:
   * 1. Returning to the same tooth position
   * 2. Transitioning between jaws (upper ↔ lower)
   */
  protected previousToothSide = computed(() => {
    const prev = this.previousTooth();
    const current = this.modifiedTooth();
    if (!prev || !current) return this.currentSide();

    // If same tooth position, previous side is opposite of current
    if (prev.position === current.position) {
      return this.currentSide() === 'buccal' ? 'lingual' : 'buccal';
    }

    // Check if we're transitioning between jaws (upper: 1-16, lower: 17-32)
    const currentIsUpperJaw = current.position <= UPPER_RIGHT_END;
    const prevIsUpperJaw = prev.position <= UPPER_RIGHT_END;
    const isJawTransition = currentIsUpperJaw !== prevIsUpperJaw;

    // If transitioning between jaws, switch sides
    if (isJawTransition) {
      return this.currentSide() === 'buccal' ? 'lingual' : 'buccal';
    }

    // If different tooth position within same jaw, stay on the same side
    return this.currentSide();
  });

  protected jawRelatedMeasurements = computed(() => this.getJawRelatedMeasurements(this.typeSafeModifedTooth()!));

  protected inputMode = toSignal(this.perioFormControlService.getInputMode$(), { initialValue: 'pocketDepth' as PerioInputMode });
  public readonly activePocketDepthCursor = toSignal(this.perioFormControlService.getActivePocketDepthCursor$());
  public readonly activeRecessionCursor = toSignal(this.perioFormControlService.getActiveRecessionCursor$());
  public readonly activeCursor = toSignal(this.perioFormControlService.getActiveCursor$());
  public readonly movedWithoutValue = signal(false);
  /**
   *  We only want to show the previous pocket depth preview when:
   *  - the user has not moved without entering a value
   *  - there is no active pocket depth cursor (we don't want to show previews when editing)
   *  - there is an active cursor (we are in pocket depth input mode)
   *  - the visual direction is left (we are moving to the previous tooth)
   *  - there is a previous tooth to move to
   *  - the input mode is pocket depth
   */
  public readonly showPreviousPocketDepth = computed(() => {
    return (
      !this.movedWithoutValue() &&
      !this.activePocketDepthCursor() &&
      this.activeCursor() &&
      this.visualDirection() === 'left' &&
      this.previousTooth() &&
      this.inputMode() === 'pocketDepth'
    );
  });
  public readonly showNextPocketDepth = computed(() => {
    return (
      !this.movedWithoutValue() &&
      !this.activePocketDepthCursor() &&
      this.activeCursor() &&
      this.visualDirection() === 'right' &&
      this.nextTooth() &&
      this.inputMode() === 'pocketDepth'
    );
  });
  public showPreviousRecession = computed(() => {
    return (
      !this.movedWithoutValue() &&
      !this.activeRecessionCursor() &&
      this.visualDirection() === 'left' &&
      this.previousTooth() &&
      this.inputMode() === 'recession'
    );
  });
  public showNextRecession = computed(() => {
    return (
      !this.movedWithoutValue() &&
      !this.activeRecessionCursor() &&
      this.visualDirection() === 'right' &&
      this.nextTooth() &&
      this.inputMode() === 'recession'
    );
  });

  protected isInputModeDropdownOpen = signal(false);

  constructor() {
    effect(() => {
      this.modifiedTooth.set(this.tooth());
    });
  }

  public ngOnInit(): void {
    this.perioFormControlService.getMovedWithoutValue$(this.destroyRef).subscribe(() => {
      this.movedWithoutValue.set(true);
    });
  }

  protected onToggleDropdown(): void {
    this.isInputModeDropdownOpen.update((isOpen) => !isOpen);
  }

  protected onSelectInputMode(mode: PerioInputMode): void {
    this.perioFormControlService.setInputMode(mode);
    this.isInputModeDropdownOpen.set(false);
  }

  protected onToggleInputMode(): void {
    const currentMode = this.perioFormControlService.getInputMode();
    const newMode: PerioInputMode = currentMode === 'pocketDepth' ? 'recession' : 'pocketDepth';
    this.perioFormControlService.setInputMode(newMode);
  }

  protected onToggleCheatSheet(): void {
    this.toggleCheatSheet.emit();
  }

  protected onPerioChanged(perio: PerioMeasurementsBySideDraft): void {
    this.movedWithoutValue.set(false);
    this.perioChange.emit(perio);
  }

  protected onJawMeasurementsChanged(measurements: PerioJawMeasurements): void {
    this.movedWithoutValue.set(false);
    this.jawMeasurementsChange.emit(measurements);
  }

  protected onToothSidePerioDone(direction: TargetIndexMovement): void {
    this.toothSidePerioDone.emit(direction);
  }

  protected onMoveToNextToothManually(): void {
    this.moveToNextTooth.emit();
  }

  protected onMoveToPreviousToothManually(): void {
    this.moveToPreviousTooth.emit();
  }

  protected onSwitchToLingualManually(): void {
    this.switchToLingual.emit();
  }

  protected onSwitchToFacialManually(): void {
    this.switchToFacial.emit();
  }

  protected onSwitchToOppositeJawLingualManually(toothNumber: number): void {
    this.switchToOppositeJawLingual.emit(toothNumber);
  }

  private extractFurcationValue(tooth: ToothPerioDraft): number {
    const buccalFurcation = tooth.perio.buccal.furcation;
    const lingualFurcation = tooth.perio.lingual.furcation;

    return (
      buccalFurcation?.central ||
      lingualFurcation?.central ||
      lingualFurcation?.distal ||
      lingualFurcation?.mesial ||
      buccalFurcation?.distal ||
      buccalFurcation?.mesial ||
      0
    );
  }

  private getJawRelatedMeasurements(tooth: ToothPerioDraft): PerioJawMeasurements {
    return {
      mob: tooth.mobility! || 0,
      fg: this.extractFurcationValue(tooth),
    };
  }
}
